import Button from "components/button/Button";
import InputComponent from "components/formInput/Input";
import Loading from "components/loading/Loading";
import { ERROR_MSG } from "constants/Constants";
import eaw from "eastasianwidth";
import { useFormik } from "formik";
import { apiChangePassword } from "helpers/api/accounts";
import Cookies from "js-cookie";
import { delay, keys, some } from "lodash";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ROUTE } from "routes/route";
import { showToastError, showToastSuccess } from "utils";
import * as yup from "yup";

const SettingPassword = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Responsive tracking
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize(); // initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { values, errors, handleChange, validateForm } = useFormik({
    validateOnChange: false,
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: yup.object().shape({
      password: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .min(8, t("messages.MSG-USP003"))
        .max(36, t("messages.MSG-USP003"))
        .test(
          "halfsize",
          t("messages.MSG-USP003"),
          (value) => !(value && eaw.length(value) !== value.length)
        ),
      confirmPassword: yup
        .string()
        .required(ERROR_MSG.REQUIRE)
        .oneOf([yup.ref("password")], t("messages.MSG-USP011")),
    }),
  });

  const handleSave = async () => {
    const isValid = Boolean(!keys(await validateForm()).length);
    if (isValid) {
      setLoading(true);
      apiChangePassword({ new_password: values.password })
        .then((res) => {
          if (res.error) {
            showToastError(res.error.message);
          } else {
            showToastSuccess("messages.MSG-USP012");
            delay(() => {
              localStorage.clear();
              Cookies.remove("USER_INFO");
              Cookies.remove("access_token");
              Cookies.remove("refresh_token");
              window.location.href = ROUTE.PUBLIC.LOGIN;
            }, 500);
          }
        })
        .catch(({ error }) => {
          showToastError(`messages.${error.reason}`);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // Inline styles
  const styles = {
    container: {
      padding: 20,
      display: "flex",
      flexDirection: "column",
    },
    title: {
      fontSize: 30,
      fontWeight: "bold",
      marginBottom: 50,
      display: "flex",
      justifyContent: "flex-start",
    },
    inputWrapper: {
      marginBottom: 50,
      minWidth: isMobile ? "100%" : "40%",
      maxWidth: isMobile ? "100%" : "60%",
    },
    label: {
      fontSize: 16,
      marginBottom: 8,
    },
    buttonWrapper: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginTop: "10vh",
    },
    buttonStyle: {
      fontSize: 20,
      fontWeight: "bold",
      borderRadius: 60,
      margin: "50px 0 0 0",
    },
  };

  return (
    <div className="notice-container main-content">
      <div className="root-right-header">
        <div className="screen-name">{t("admin_role")}</div>
      </div>
      <form style={styles.container}>
        <div style={styles.title}>{t("settingPassword.title")}</div>

        <div style={styles.inputWrapper}>
          <div style={styles.label}>{t("settingPassword.password")}</div>
          <InputComponent
            name="password"
            value={values?.password}
            error={errors?.password}
            onChange={handleChange}
          />
        </div>

        <div style={styles.inputWrapper}>
          <div style={styles.label}>{t("settingPassword.confirmPassword")}</div>
          <InputComponent
            name="confirmPassword"
            value={values?.confirmPassword}
            error={errors?.confirmPassword}
            onChange={handleChange}
          />
        </div>

        <div style={styles.buttonWrapper}>
          <Button
            text={t("settingPassword.save")}
            type="button"
            height={40}
            width={300}
            disabled={some(Object.values(values), (item) => !item)}
            onClick={handleSave}
            style={styles.buttonStyle}
          />
        </div>
      </form>
      {loading ? <Loading /> : null}
    </div>
  );
};

export default SettingPassword;
